import type { DestroyOptions } from 'pixi.js';
import { Container, Sprite, Point } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { TrayModel } from '../../models/tray/TrayModel';
import type { TileStackModel } from '../../models/board/TileStackModel';
import { waitForMs } from '../../../../utils/async-utils';
import { soundManager } from '../../../../app/SoundManager';
import { Easing } from '../../../../libs/TweenManager/easing';
import { setContainerParent } from '../../../../utils/pixi/transform-utils';
import MathUtils from '../../../../utils/MathUtils';
import { TrayStackView } from './TrayStackView';
import type { TileStackView } from '../board/TileStackView';
import type { BoardViewFactory } from '../board/BoardViewFactory';
import { TileStackHighlight } from '../board/TileStackHighlight';

export interface TrayViewSettings {
  readonly stackSpacing: number;
  readonly animate: boolean;
}

export interface TrayItemView {
  readonly stack: TrayStackView;
  readonly trayPosition: Point;
  readonly highlight: TileStackHighlight;
  readonly background: Sprite;
}

export class TrayView extends Container {
  private static readonly nextItemDelay = 150;

  private static readonly animateToSnapDuration = 100;

  private readonly items: TrayItemView[];

  private readonly stackContainer = new Container();

  public readonly onItemSelected = new Signal<(item: TrayStackView) => void>();

  constructor(
    private readonly viewFactory: BoardViewFactory,
    model: TrayModel,
    private readonly settings: TrayViewSettings,
  ) {
    super();

    this.addChild(this.stackContainer);
    this.stackContainer.sortableChildren = true;
    this.items = this.createItemViews(model.getItems());

    model.onRefilled.connect(() => {
      this.animateRefill();
    });
  }

  public destroy(options?: DestroyOptions): void {
    this.onItemSelected.disconnectAll();
    super.destroy(options);
  }

  public animateRefill(animateBackgrounds = false): void {
    this.items.forEach((item, index) => {
      const delay = index * TrayView.nextItemDelay;
      this.animateItemIn(item, animateBackgrounds, delay);
    });
  }

  private createItemViews(items: TileStackModel[]): TrayItemView[] {
    const { stackSpacing } = this.settings;

    const startX = stackSpacing * (items.length - 1) * -0.5;

    return items.map((model, index) => {
      const stackView = new TrayStackView(model, this.viewFactory).props({
        x: startX + stackSpacing * index,
        parent: this.stackContainer,
      });

      stackView.onPointerDown.connect(() => {
        this.onItemSelected.emit(stackView);
      });

      const item: TrayItemView = {
        stack: stackView,
        trayPosition: stackView.position.clone(),
        highlight: new TileStackHighlight().props({
          x: stackView.x,
          y: stackView.y,
          addAtIndex: 0,
          parent: this,
        }),
        background: Sprite.from('tile_empty').props({
          x: stackView.x,
          y: stackView.y,
          anchor: 0.5,
          addAtIndex: 0,
          parent: this,
        }),
      };
      return item;
    });
  }

  private animateItemIn(item: TrayItemView, animateBackground: boolean, delay: number, onComplete?: () => void): void {
    const { stack, background } = item;
    const soundParams = { volume: 0.025 };

    if (stack.destroyed) return;
    stack.visible = true;
    stack.scale.set(0.3, 0.3);
    stack.alpha = 0;
    stack.zIndex = 0;

    stack.updateTiles();

    const fadeTween = stack.tweenTo({ alpha: 1 }, 150, 'quadOut', true, delay);
    // TODO fix on start
    waitForMs(delay).then(
      () => {
        soundManager.play('Gameplay_Stack_Placement', soundParams);
      },
      () => {},
    );

    if (onComplete)
      fadeTween.onComplete.addOnce(async () => {
        // FIXME without wait tutorial hand blinks at wrong position
        await waitForMs(1);
        onComplete();
      });
    fadeTween.start();

    if (animateBackground && background) {
      background.alpha = 0;
      background.tweenTo({ alpha: 1 }, 150, 'quadOut', true, delay + 150);
    }

    stack.animateToScale(false, 1, delay, 1000, Easing.Elastic.Out);
  }

  public animatePickingStackUp(stack: TrayStackView, scale: number): void {
    stack.animateToScale(true, scale);
    stack.zIndex = 100;
    soundManager.play(`Gameplay_Stack_Pickup`);
  }

  public animateReturnToTray(stackView: TrayStackView, animate = true): void {
    const item = this.getItemByStackView(stackView);
    if (!item) return;

    const { stack, trayPosition: toPos } = item;
    setContainerParent(stack, this.stackContainer);

    if (animate) {
      const { position: fromPos } = stack;
      const distance = MathUtils.getDistance(fromPos.x, fromPos.y, toPos.x, toPos.y);
      const duration = MathUtils.clamp(distance * 1.5, 150, 300);
      const toPosLocal = stack.parent?.toLocal(this.toGlobal(toPos)) ?? new Point();

      stack.locked = true;
      stack.animateToScale(false, 1, 0, duration, Easing.Quadratic.Out);
      stack.tweenTo({ x: toPosLocal.x, y: toPosLocal.y }, duration, 'quadOut', true).onComplete.addOnce(() => {
        stack.locked = false;
        stack.zIndex = 0;
      });
      item.highlight.hide();
    } else {
      stack.position.set(toPos.x, toPos.y);
      stack.zIndex = 0;
      item.highlight.hide(0);
    }
  }

  public async animateTileToBoard(stackView: TrayStackView, toStack: TileStackView): Promise<void> {
    soundManager.play(`Gameplay_Stack_Placement`);

    const item = this.getItemByStackView(stackView);
    if (!item) return;
    if (!stackView.parent || !toStack.parent) return;

    const toPos = stackView.parent.toLocal(toStack.position, toStack.parent);
    await stackView
      .tweenTo({ x: toPos.x, y: toPos.y }, TrayView.animateToSnapDuration, 'quadOut', true)
      .asyncOnComplete();

    stackView.visible = false;
    this.animateReturnToTray(stackView, false);
  }

  public updateItemHighlight(stackView: TrayStackView, showHighlight: boolean): void {
    const item = this.getItemByStackView(stackView);
    if (!item) return;

    if (showHighlight) item.highlight.show(750, 100);
    else item.highlight.hide(500);
  }

  private getItemByStackView(stackView: TrayStackView): TrayItemView | null {
    return this.items.find((item: TrayItemView) => item.stack === stackView) ?? null;
  }

  public getItemByIndex(index: number): TrayItemView | null {
    return this.items.at(index) ?? null;
  }
}
